import React, { useState, useEffect, createContext, useContext } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Fade,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  Alert,
  Snackbar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import {
  ArrowBack,
  Person,
  Share,
  Settings,
  TrendingUp,
  CheckCircle,
  Close as CloseIcon,
  AttachMoney,
  FilterAlt as FilterIcon,
  Timeline,
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { useParams, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useQuery } from 'react-query';
import API from '../../API';
import AlertDialog from '../../Components/AlertDialog';
import useAlertDialog from '../../Components/useAlertDialog';
import { CreatorProvider } from '../../Hooks/agencySelectedCreatorContext';
import PageTitle from '../../Components/PageTitle';
import { toast } from 'sonner';
import AgencyCreatorSettings from './agencyCreatorTabs/agencyCreatorEdit';
import AgencyCreatorAllActivity from './agencyCreatorTabs/AgencyCreatorAllActivity';

/** ---------- CONSTANTS ---------- */
const APPSHELL_BAR_HEIGHT = 86;
const APPSHELL_EXTRA_GUTTER = 12;

/** ---------- FILTER CONTEXT ---------- */
const ActivityFilterContext = createContext({
  activeFilter: null,
  setActiveFilter: () => {},
});

export const useActivityFilter = () => useContext(ActivityFilterContext);

/** ---------- SAFETY HELPERS ---------- */
const toText = (v) => {
  if (v == null) return '';
  const t = typeof v;
  if (t === 'string' || t === 'number') return String(v);
  if (t === 'boolean') return v ? 'Yes' : 'No';
  if (t === 'object') {
    const candidate =
      v.username ?? v.handle ?? v.name ?? v.title ?? v.display_name ?? v.id ?? '';
    return candidate ? String(candidate) : '';
  }
  return '';
};

const toNum = (v, def = 0) => {
  if (v == null) return def;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};

const getHandle = (data) => {
  if (!data) return 'unknown';
  const raw =
    data.handle ??
    data.username ??
    data.creator ??
    data.instagram ??
    data.twitter ??
    data.x_link ??
    data.youtube ??
    '';
  const txt = toText(raw);
  const clean = txt.replace(/^@/, '').trim();
  return clean || 'unknown';
};

const getDisplayName = (data) => {
  if (!data) return 'Unknown Creator';
  const txt = toText(data.display_name) || toText(data.creator);
  return txt || 'Unknown Creator';
};

/** ---------- COMPACT CREATOR HEADER ---------- */
const CompactCreatorHeader = ({ creatorData, activityData, isLoading, isMobile, onShareClick, onSettingsClick, onBioClick }) => {
  const theme = useTheme();
  const { activeFilter, setActiveFilter } = useActivityFilter();

  if (isLoading) {
    return (
      <Card>
        <CardContent sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Skeleton variant="circular" width={56} height={56} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="40%" height={32} />
              <Skeleton variant="text" width="30%" height={20} />
            </Box>
            <Stack direction="row" spacing={1}>
              <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 2 }} />
              <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 2 }} />
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (!creatorData) return null;

  const avatarSrc = toText(creatorData.pfphref);
  const displayName = getDisplayName(creatorData);
  const handle = getHandle(creatorData);
  const statusText = toText(creatorData.status);
  const isVerified = !!creatorData.creator_user;

  // Calculate all stats from activity data if available
  let totalCampaigns = toNum(creatorData.total_campaigns, 0);
  let totalEarnings = toNum(creatorData.total_earnings, 0);
  let completionRate = toNum(creatorData.completion_rate, 0);
  
  if (activityData?.activities) {
    const activities = activityData.activities;
    
    // Count unique campaigns and partnerships
    const uniqueActivities = new Set();
    activities.forEach(a => {
      if (a.activity_type === 'Campaign' || a.activity_type === 'Partnership') {
        uniqueActivities.add(`${a.activity_type}-${a.activity_id}`);
      }
    });
    totalCampaigns = uniqueActivities.size;
    
    // Sum all earnings
    totalEarnings = activities.reduce((sum, a) => {
      if (a.activity_type === 'Payout') {
        return sum + toNum(a.net_amount, 0);
      }
      return sum;
    }, 0);
    
    
    // Calculate completion rate
    const completedActivities = activities.filter(a => {
      const status = (a.creator_status || a.campaign_status || '').toLowerCase();
      return status === 'completed' || a.activity_type === 'Payout';
    });
    if (activities.length > 0) {
      completionRate = Math.round((completedActivities.length / activities.length) * 100);
    }
  }

  const getCreatorInitial = () => {
    const name = displayName || handle;
    return name && typeof name === 'string' ? name.charAt(0).toUpperCase() : '?';
  };

  const handleFilterClick = (filterType) => {
    // Toggle filter - if clicking the same filter, clear it
    setActiveFilter(activeFilter === filterType ? null : filterType);
  };

  return (
    <Card>
      <CardContent sx={{ p: 2 }}>
        <Stack 
          direction={isMobile ? 'column' : 'row'} 
          spacing={2} 
          alignItems={isMobile ? 'flex-start' : 'center'}
        >
          {/* Left: Avatar + Name */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: isMobile ? 1 : 0 }}>
            <Tooltip title="Click to view bio">
              <Avatar
                src={avatarSrc || undefined}
                onClick={onBioClick}
                sx={{
                  width: 56,
                  height: 56,
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    borderColor: theme.palette.primary.main,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                  }
                }}
              >
                {getCreatorInitial()}
              </Avatar>
            </Tooltip>

            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ lineHeight: 1.2 }}
                  noWrap
                >
                  {displayName}
                </Typography>
                {isVerified && (
                  <CheckCircle color="primary" fontSize="small" />
                )}
              </Stack>

              <Typography variant="body2" color="text.secondary" noWrap>
                @{handle}
              </Typography>
            </Box>
          </Stack>

          {/* Center: Interactive Stats Chips */}
          {!isMobile && (
            <Stack direction="row" spacing={1} sx={{ flex: 1 }}>
              <Tooltip title="Click to filter by campaigns">
                <Chip
                  icon={<Timeline fontSize="small" />}
                  label={`${totalCampaigns} collabs`}
                  size="small"
                  variant={activeFilter === 'campaigns' ? 'filled' : 'outlined'}
                  color="primary"
                  onClick={() => handleFilterClick('campaigns')}
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: 1,
                    }
                  }}
                />
              </Tooltip>
              <Tooltip title="Click to filter by earnings">
                <Chip
                  icon={<AttachMoney fontSize="small" />}
                  label={`$${totalEarnings.toLocaleString()}`}
                  size="small"
                  variant={activeFilter === 'earnings' ? 'filled' : 'outlined'}
                  color="success"
                  onClick={() => handleFilterClick('earnings')}
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: 1,
                    }
                  }}
                />
              </Tooltip>
              <Tooltip title="Click to filter by completed activities">
                <Chip
                  icon={<TrendingUp fontSize="small" />}
                  label={`${completionRate}% success`}
                  size="small"
                  variant={activeFilter === 'completed' ? 'filled' : 'outlined'}
                  color="info"
                  onClick={() => handleFilterClick('completed')}
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: 1,
                    }
                  }}
                />
              </Tooltip>
              {activeFilter && (
                <Chip
                  icon={<CloseIcon fontSize="small" />}
                  label="Clear"
                  size="small"
                  variant="outlined"
                  onClick={() => setActiveFilter(null)}
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                />
              )}
            </Stack>
          )}

          {/* Right: Status + Actions */}
          <Stack direction="row" spacing={1} alignItems="center">
            {statusText && (
              <Chip
                label={statusText}
                size="small"
                color={statusText.toLowerCase() === 'active' ? 'success' : 'default'}
                sx={{ fontWeight: 600 }}
              />
            )}
            <Chip
              label={isVerified ? 'Partner' : 'Associate'}
              size="small"
              variant="outlined"
              color={isVerified ? 'primary' : 'default'}
            />
            
            <Tooltip title="Edit Settings">
              <IconButton 
                size="small" 
                color="primary" 
                onClick={onSettingsClick}
                sx={{ ml: 1 }}
              >
                <Settings fontSize="small" />
              </IconButton>
            </Tooltip>

            {!isMobile && (
              <Tooltip title="Share Profile">
                <IconButton size="small" color="primary" onClick={onShareClick}>
                  <Share fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        {/* Mobile Stats */}
        {isMobile && (
          <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }} useFlexGap>
            <Chip
              icon={<Timeline fontSize="small" />}
              label={`${totalCampaigns} campaigns`}
              size="small"
              variant={activeFilter === 'campaigns' ? 'filled' : 'outlined'}
              color="primary"
              onClick={() => handleFilterClick('campaigns')}
              sx={{ cursor: 'pointer' }}
            />
            <Chip
              icon={<AttachMoney fontSize="small" />}
              label={`$${totalEarnings.toLocaleString()}`}
              size="small"
              variant={activeFilter === 'earnings' ? 'filled' : 'outlined'}
              color="success"
              onClick={() => handleFilterClick('earnings')}
              sx={{ cursor: 'pointer' }}
            />
            <Chip
              icon={<TrendingUp fontSize="small" />}
              label={`${completionRate}% success`}
              size="small"
              variant={activeFilter === 'completed' ? 'filled' : 'outlined'}
              color="info"
              onClick={() => handleFilterClick('completed')}
              sx={{ cursor: 'pointer' }}
            />
            {activeFilter && (
              <Chip
                icon={<CloseIcon fontSize="small" />}
                label="Clear"
                size="small"
                variant="outlined"
                onClick={() => setActiveFilter(null)}
                sx={{ cursor: 'pointer' }}
              />
            )}
          </Stack>
        )}

        {/* Active Filter Banner */}
        {activeFilter && (
          <Alert 
            severity="info" 
            icon={<FilterIcon />}
            sx={{ mt: 2 }}
            onClose={() => setActiveFilter(null)}
          >
            Filtering by: <strong>
              {activeFilter === 'campaigns' && 'All Campaigns'}
              {activeFilter === 'earnings' && 'Activities with Earnings'}
              {activeFilter === 'completed' && 'Completed Activities'}
            </strong>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

/** ---------- SETTINGS DIALOG ---------- */
const CreatorSettingsDialog = ({ open, onClose, creatorData, onUpdate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}  // Add this - makes it full-screen on mobile
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          borderRadius: 3,
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: `1px solid ${theme.palette.divider}`,
        pb: 2
      }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Settings color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Creator Settings
          </Typography>
        </Stack>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ 
          maxHeight: 'calc(90vh - 80px)', 
          overflowY: 'auto',
        }}>
          <AgencyCreatorSettings 
            creatorData={creatorData} 
            onUpdate={() => {
              onUpdate?.();
              onClose();
            }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const LoadingState = () => (
  <Container maxWidth="lg" sx={{ py: 4 }}>
    <Stack spacing={2}>
      <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1 }} />
      <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
    </Stack>
  </Container>
);

export default function AgencyCreator() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { creatorId } = useParams();
  const { dialogState, openDialog, closeDialog } = useAlertDialog();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null);

  // Check if we're on the bio route
  const isBioRoute = location.pathname.includes('/bio');

  const {
    data: creatorData,
    isLoading,
    refetch,
    error,
  } = useQuery(['creators', creatorId], () => API.agency.getCreator(creatorId), {
    refetchOnWindowFocus: false,
    enabled: !!creatorId,
    retry: 2,
  });

  // Fetch activity data for completion rate calculation
  const { data: activityData } = useQuery({
    queryKey: ['agency', 'creator-activity', creatorId, 'all'],
    queryFn: () => API.agency.getCreatorActivity(creatorId, { filter: 'all' }),
    refetchOnWindowFocus: false,
    enabled: !!creatorId,
  });

  useEffect(() => {
    if (error) {
      openDialog(
        'Error Loading Creator',
        `Unable to load creator data: ${error?.response?.data?.error || error.message}`,
        closeDialog,
        closeDialog,
        'OK',
        null
      );
    }
  }, [error, openDialog, closeDialog]);

  const handleRefresh = () => {
    refetch();
    setSnackbarOpen(true);
  };

  const handleShare = () => {
    const url = `${window.location.origin}/creators/${creatorId}`;
    navigator.clipboard.writeText(url).then(() => {
      toast.success('Creator profile link copied to clipboard');
    }).catch(() => {
      toast.error('Failed to copy link');
    });
  };

  const handleOpenSettings = () => {
    setSettingsDialogOpen(true);
  };

  const handleCloseSettings = () => {
    setSettingsDialogOpen(false);
  };

  const handleSettingsUpdate = () => {
    refetch();
    handleCloseSettings();
  };

  const handleBioClick = () => {
    navigate(`/agency/creators/${creatorId}/bio`);
  };

  if (isLoading) {
    return <LoadingState />;
  }

  const filterContextValue = {
    activeFilter,
    setActiveFilter,
  };

  // Mobile Layout
  if (isMobile) {
    return (
      <>
        <PageTitle title={`${toText(creatorId)} - Agency - Blitz`} />

        <AppBar position="static" elevation={0} color="default" sx={{ pt: 'env(safe-area-inset-top, 0px)' }}>
          <Toolbar>
            <IconButton edge="start" onClick={() => navigate('/agency/creators')}>
              <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1, ml: 1 }}>
              <Typography variant="h6" noWrap>
                {getDisplayName(creatorData?.creator) || toText(creatorId)}
              </Typography>
            </Box>
            <Tooltip title="View Bio">
              <IconButton onClick={handleBioClick}>
                <Person />
              </IconButton>
            </Tooltip>
            <IconButton onClick={handleOpenSettings}>
              <Settings />
            </IconButton>
            <IconButton onClick={handleShare}>
              <Share />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Box 
          sx={{ 
            minHeight: '100vh',
            pb: `calc(${APPSHELL_BAR_HEIGHT}px + ${APPSHELL_EXTRA_GUTTER}px + env(safe-area-inset-bottom, 0px))`,
          }}
        >
          <Container maxWidth="sm" sx={{ py: 2 }}>
            <ActivityFilterContext.Provider value={filterContextValue}>
              <CompactCreatorHeader 
                creatorData={creatorData?.creator}
                activityData={activityData}
                isLoading={isLoading} 
                isMobile 
                onShareClick={handleShare}
                onSettingsClick={handleOpenSettings}
                onBioClick={handleBioClick}
              />
            </ActivityFilterContext.Provider>
          </Container>

          <ActivityFilterContext.Provider value={filterContextValue}>
            <CreatorProvider value={{ creatorData, refetch }}>
              <Box>
                {isBioRoute ? (
                  <Outlet />
                ) : (
                  <AgencyCreatorAllActivity />
                )}
              </Box>
            </CreatorProvider>
          </ActivityFilterContext.Provider>
        </Box>

        <CreatorSettingsDialog
          open={settingsDialogOpen}
          onClose={handleCloseSettings}
          creatorData={creatorData}
          onUpdate={handleSettingsUpdate}
        />

        <AlertDialog alertState={dialogState} />

        <Snackbar open={snackbarOpen} autoHideDuration={2000} onClose={() => setSnackbarOpen(false)}>
          <Alert severity="success" onClose={() => setSnackbarOpen(false)}>
            Creator data refreshed
          </Alert>
        </Snackbar>
      </>
    );
  }

  // Desktop Layout
  return (
    <>
      <PageTitle title={`${toText(creatorId)} - Agency - Blitz`} />

      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Container maxWidth="lg" sx={{ py: 2 }}>
          <Fade in={!isLoading} timeout={400}>
            <Box>
              <ActivityFilterContext.Provider value={filterContextValue}>
                <CompactCreatorHeader 
                  creatorData={creatorData?.creator}
                  activityData={activityData}
                  isLoading={isLoading} 
                  isMobile={false}
                  onShareClick={handleShare}
                  onSettingsClick={handleOpenSettings}
                  onBioClick={handleBioClick}
                />
              </ActivityFilterContext.Provider>
            </Box>
          </Fade>
        </Container>

        <Box sx={{ flex: 1, overflow: 'auto', bgcolor: 'background.default' }}>
          <ActivityFilterContext.Provider value={filterContextValue}>
            <CreatorProvider value={{ creatorData, refetch }}>
              <Fade in={!isLoading} timeout={600}>
                <Box sx={{ minHeight: '100%' }}>
                  {isBioRoute ? (
                    <Outlet />
                  ) : (
                    <AgencyCreatorAllActivity />
                  )}
                </Box>
              </Fade>
            </CreatorProvider>
          </ActivityFilterContext.Provider>
        </Box>
      </Box>

      <CreatorSettingsDialog
        open={settingsDialogOpen}
        onClose={handleCloseSettings}
        creatorData={creatorData}
        onUpdate={handleSettingsUpdate}
      />

      <AlertDialog alertState={dialogState} />

      <Snackbar open={snackbarOpen} autoHideDuration={2000} onClose={() => setSnackbarOpen(false)}>
        <Alert severity="success" onClose={() => setSnackbarOpen(false)}>
          Creator data refreshed
        </Alert>
      </Snackbar>
    </>
  );
}